import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { 
  LOGIN_MUTATION, 
  LOGOUT_MUTATION, 
  CURRENT_USER_QUERY,
  User,
  LoginInput,
  LoginResponse,
  LogoutResponse,
  CurrentUserResponse 
} from '../graphql/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage for persisted auth state
    return localStorage.getItem('verit_authenticated') === 'true';
  });
  const [user, setUser] = useState<User | null>(() => {
    // Check localStorage for persisted user data
    const savedUser = localStorage.getItem('verit_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [authChecked, setAuthChecked] = useState(false);
  
  // GraphQL hooks with better error handling
  const { data: currentUserData, loading: userLoading, refetch: refetchUser, error } = useQuery<CurrentUserResponse>(CURRENT_USER_QUERY, {
    errorPolicy: 'ignore', // Don't throw errors if user is not authenticated
    fetchPolicy: 'cache-and-network', // Always try to fetch fresh data
    notifyOnNetworkStatusChange: true,
  });
  
  // Fetch CSRF token on app startup and setup periodic refresh
  React.useEffect(() => {
    const apiBase = (process.env.REACT_APP_API_URL ?? '').replace(/\/$/, '');
    const csrfUrl = apiBase ? `${apiBase}/api/v1/csrf-token/` : '/api/v1/csrf-token/';
    const fetchCSRF = () => {
      fetch(csrfUrl, { credentials: 'include' })
        .then(response => {
          if (response.ok) {
            console.log('CSRF token fetched successfully');
          } else {
            console.log('CSRF token fetch failed:', response.status);
          }
        })
        .catch(error => console.log('CSRF token fetch error:', error));
    };
    
    // Initial fetch
    fetchCSRF();
    
    // Refresh CSRF token every 30 minutes
    const csrfInterval = setInterval(fetchCSRF, 30 * 60 * 1000);
    
    return () => clearInterval(csrfInterval);
  }, []);
  
  // Periodic session validation
  React.useEffect(() => {
    if (isAuthenticated) {
      const validateSession = () => {
        refetchUser().catch(() => {
          console.log('Session validation failed, but keeping auth state');
          // Don't auto-logout on network errors
        });
      };
      
      // Validate session every 5 minutes
      const sessionInterval = setInterval(validateSession, 5 * 60 * 1000);
      
      return () => clearInterval(sessionInterval);
    }
  }, [isAuthenticated, refetchUser]);
  const [loginMutation] = useMutation<LoginResponse>(LOGIN_MUTATION);
  const [logoutMutation] = useMutation<LogoutResponse>(LOGOUT_MUTATION);

  useEffect(() => {
    // Only update auth state after we've actually checked with the server
    if (!userLoading) {
      setAuthChecked(true);
      if (currentUserData?.currentUser) {
        setUser(currentUserData.currentUser);
        setIsAuthenticated(true);
        // Persist to localStorage
        localStorage.setItem('verit_authenticated', 'true');
        localStorage.setItem('verit_user', JSON.stringify(currentUserData.currentUser));
        console.log('User authenticated:', currentUserData.currentUser.username);
      } else {
        // Only clear auth state if we're sure there's no user (not just a network error)
        if (!error) {
          // No error, just no user data - this means user is not logged in
          if (isAuthenticated) {
            console.log('User was authenticated but server says no current user - logging out');
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('verit_authenticated');
            localStorage.removeItem('verit_user');
          }
        } else if (error.networkError) {
          console.log('Network error - keeping existing auth state:', error.networkError);
          // Don't clear auth state on network errors
        } else {
          console.log('GraphQL error - clearing auth state:', error);
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('verit_authenticated');
          localStorage.removeItem('verit_user');
        }
      }
    }
  }, [currentUserData, userLoading, error]);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const { data } = await loginMutation({
        variables: {
          input: { username, password } as LoginInput
        }
      });

      if (data?.login.success && data.login.user) {
        setUser(data.login.user);
        setIsAuthenticated(true);
        // Persist to localStorage
        localStorage.setItem('verit_authenticated', 'true');
        localStorage.setItem('verit_user', JSON.stringify(data.login.user));
        // Refetch user data to ensure session is properly established
        await refetchUser();
        console.log('Login successful, user persisted');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await logoutMutation();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state and localStorage
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('verit_authenticated');
      localStorage.removeItem('verit_user');
      console.log('Logout complete, auth state cleared');
      
      // Hard refresh to clear all cached data and Apollo cache
      window.location.href = '/login';
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading: userLoading || !authChecked,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
